// User Recognition Service - handles UUID lookup across pending_payments and users tables
import axios from 'axios';
import { cookieManager } from '../utils/cookieManager';
import { getBaseApiUrl, getCorsSettings } from '../utils/apiConfig';
import { handle401Error } from '../utils/handle401Error';

const API_BASE_URL = getBaseApiUrl();
const corsSettings = getCorsSettings();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  ...corsSettings,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 authentication errors
    if (error.response?.status === 401) {
      handle401Error();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Session-level cache for user recognition status (avoids re-checking on every route change)
let _recognitionCache = { uuid: null, data: null, expiresAt: 0 };
const RECOGNITION_CACHE_TTL = 60000; // 60 seconds

export const userRecognitionService = {
  /**
   * Check if UUID exists and determine user status
   * @param {string} uuid - UUID to lookup
   * @returns {object} - User status and redirect information
   */
  async checkUserStatus(uuid) {
    if (!uuid) {
      return {
        exists: false,
        status: 'new',
        redirectTo: null,
        message: 'No UUID provided'
      };
    }

    // Return cached result if same UUID and not expired
    if (
      _recognitionCache.uuid === uuid &&
      _recognitionCache.data &&
      Date.now() < _recognitionCache.expiresAt
    ) {
      return _recognitionCache.data;
    }

    try {
      const response = await api.get(`/user-recognition/check/${uuid}`);
      _recognitionCache = {
        uuid,
        data: response.data,
        expiresAt: Date.now() + RECOGNITION_CACHE_TTL,
      };
      return response.data;
    } catch (error) {
      console.error('User status check failed:', error);
      return {
        exists: false,
        status: 'error',
        redirectTo: null,
        message: error.response?.data?.error || 'Failed to check user status'
      };
    }
  },


  /**
   * Link existing payment to authenticated user
   * @param {string} uuid - UUID from cookie
   * @param {string} authToken - Authentication token
   * @returns {object} - Linking result
   */
  async linkPaymentToUser(uuid, authToken) {
    try {
      const response = await api.post('/user-recognition/link-payment', {
        uuid,
        auth_token: authToken
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to link payment to user:', error);
      throw new Error(error.response?.data?.error || 'Failed to link payment to user');
    }
  },

  /**
   * Get user details by UUID (from either table)
   * @param {string} uuid - UUID to lookup
   * @returns {object} - User details
   */
  async getUserByUuid(uuid) {
    if (!uuid) {
      throw new Error('UUID is required');
    }

    try {
      const response = await api.get(`/user-recognition/user/${uuid}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get user by UUID:', error);
      throw new Error(error.response?.data?.error || 'Failed to get user details');
    }
  },

  /**
   * Transfer UUID from pending_payments to users table
   * @param {string} uuid - UUID to transfer
   * @param {object} userData - User account data
   * @returns {object} - Transfer result
   */
  async transferUuidToUser(uuid, userData) {
    try {
      const response = await api.post('/user-recognition/transfer-uuid', {
        uuid,
        user_data: userData
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to transfer UUID to user:', error);
      throw new Error(error.response?.data?.error || 'Failed to transfer UUID to user');
    }
  }
};

// Helper functions for user recognition flow
export const userRecognitionHelpers = {
  /**
   * Initialize user recognition on app startup
   * @returns {object} - User status and recommended action
   */
  async initializeUserRecognition() {
    const uuid = cookieManager.getUserUuid();
    
    if (!uuid) {
      return {
        action: 'show_purchase_flow',
        status: 'new_visitor',
        message: 'New visitor - show normal purchase flow'
      };
    }

    // Check user status with existing UUID
    const userStatus = await userRecognitionService.checkUserStatus(uuid);
    
    switch (userStatus.status) {
      case 'confirmed_user':
        return {
          action: 'redirect_dashboard',
          status: 'returning_user',
          message: 'Returning user - redirect to dashboard',
          redirectTo: '/dashboard'
        };
        
      case 'pending_payment':
        return {
          action: 'show_payment_status',
          status: 'pending_payment',
          message: 'User has pending payment - show payment completion flow',
          redirectTo: '/payment/status',
          paymentData: userStatus.paymentData
        };

      case 'payment_linked_to_user':
        return {
          action: 'redirect_dashboard',
          status: 'payment_linked_to_user',
          message: 'Payment is linked to user account - redirect to dashboard',
          redirectTo: '/dashboard',
          paymentData: userStatus.paymentData
        };

      case 'order_processed':
        return {
          action: 'show_success_options',
          status: 'order_processed',
          message: 'Order has been processed - show success page with install/dashboard options',
          redirectTo: '/newuser/success',
          paymentData: userStatus.paymentData
        };
        
      case 'expired_payment':
        return {
          action: 'show_purchase_flow',
          status: 'expired_payment',
          message: 'Previous payment expired - show new purchase flow'
        };
        
      default:
        return {
          action: 'show_purchase_flow',
          status: 'unknown',
          message: 'Unknown user status - show purchase flow'
        };
    }
  },


  /**
   * Handle successful authentication by linking existing payment
   * @param {string} authToken - Authentication token
   * @returns {object} - Linking result
   */
  async handleAuthentication(authToken) {
    const uuid = cookieManager.getUserUuid();
    
    if (!uuid) {
      return {
        success: false,
        message: 'No UUID found - user may not have a pending payment'
      };
    }

    try {
      const result = await userRecognitionService.linkPaymentToUser(uuid, authToken);
      return result;
    } catch (error) {
      console.error('Authentication handling failed:', error);
      throw error;
    }
  },

  /**
   * Clear user recognition data (logout)
   */
  clearUserRecognition() {
    cookieManager.clearUserUuid();
    localStorage.removeItem('purchase_flow_state');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
  },

  /**
   * Debug information for troubleshooting
   * @returns {object} - Debug information
   */
  getDebugInfo() {
    const cookieInfo = cookieManager.getCookieInfo();
    const purchaseState = localStorage.getItem('purchase_flow_state');
    const authToken = localStorage.getItem('auth_token');
    
    return {
      cookie: cookieInfo,
      purchaseState: purchaseState ? JSON.parse(purchaseState) : null,
      hasAuthToken: !!authToken,
      localStorage: {
        keys: Object.keys(localStorage),
        purchaseFlowState: !!localStorage.getItem('purchase_flow_state'),
        authToken: !!localStorage.getItem('auth_token'),
        userId: !!localStorage.getItem('user_id')
      }
    };
  }
};

export default userRecognitionService;